import React from "react";
import { Box, IconButton, Skeleton, Typography } from "@mui/material";
import { CloseBig } from "@tiny/icons";
import { useAiSummaryPanel } from "./AiSummaryPanel.hook";

const FALLBACK_PLACEHOLDER =
  "Your AI summary is being prepared. Check back soon.";

function AiSummaryPanel(): JSX.Element | null {
  const { summary, placeholder, isLoading, isError, dismissed, dismiss } =
    useAiSummaryPanel();

  if (dismissed || isError) return null;

  if (isLoading) {
    return (
      <Skeleton
        variant="rectangular"
        width="100%"
        height={96}
        sx={{ borderRadius: "10px" }}
      />
    );
  }

  const displayText = summary ?? placeholder ?? FALLBACK_PLACEHOLDER;
  const isPlaceholder = !summary;

  return (
    <Box
      sx={{
        background: "linear-gradient(135deg, #e8f4fd 0%, #ede7f6 100%)",
        border: "1px solid #c5cae9",
        borderRadius: "10px",
        py: 1.5,
        px: 2,
        position: "relative",
      }}
    >
      <IconButton
        size="small"
        onClick={dismiss}
        aria-label="Dismiss AI summary"
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          width: 24,
          height: 24,
          background: "rgba(255,255,255,0.7)",
          border: "1px solid #c5cae9",
          "&:hover": { background: "#fff" },
        }}
      >
        <CloseBig sx={{ fontSize: 12, color: "#7986cb" }} />
      </IconButton>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, pr: 4 }}>
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: "#3f51b5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontSize: 13,
            color: "#fff",
          }}
        >
          ✦
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: "#3f51b5",
            textTransform: "uppercase",
            letterSpacing: "0.6px",
          }}
        >
          Your Account Summary
        </Typography>
      </Box>

      <Typography
        variant="p2"
        sx={{
          lineHeight: 1.65,
          color: isPlaceholder ? "text.disabled" : "text.primary",
          fontStyle: isPlaceholder ? "italic" : "normal",
          pr: 1,
        }}
      >
        {displayText}
      </Typography>
    </Box>
  );
}

export default AiSummaryPanel;
